import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { BarChart3, LogOut, Receipt, ScanLine, Wallet } from "lucide-react";
import { useI18n } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import LanguageSwitcher from "../LanguageSwitcher";
import NotificationBell from "../NotificationBell";

const TABS = [
  { to: "/me", labelKey: "nav.myScan", icon: ScanLine, testId: "employee-nav-scan" },
  { to: "/me/receipts", labelKey: "nav.myReceipts", icon: Receipt, testId: "employee-nav-receipts" },
  // refunds/payouts only exist when the parent organisation is a fleet owner
  { to: "/me/refunds", labelKey: "nav.myRefunds", icon: Wallet, testId: "employee-nav-refunds",
    payoutsOnly: true },
];

export const EmployeeShell = ({ title, subtitle, children }) => {
  const { t } = useI18n();
  const { user, tenant, logout, isAccountingFirm } = useAuth();
  const navigate = useNavigate();
  const tabs = TABS.filter((tab) => !(tab.payoutsOnly && isAccountingFirm));

  return (
    <div className="min-h-screen bg-background pb-[calc(env(safe-area-inset-bottom)+80px)]">
      <header className="sticky top-0 z-20 border-b bg-card">
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <BarChart3 className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="truncate font-display text-sm font-semibold">{tenant?.name}</p>
              <p className="truncate text-xs text-muted-foreground">{user?.name}</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <NotificationBell />
            <LanguageSwitcher showIcon={false} />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                logout();
                navigate("/login");
              }}
              data-testid="employee-logout-button"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="px-4 py-4">
        {title ? (
          <div className="mb-4">
            <h1 className="font-display text-xl font-semibold" data-testid="page-title">
              {title}
            </h1>
            {subtitle ? <p className="text-sm text-muted-foreground">{subtitle}</p> : null}
          </div>
        ) : null}
        {children}
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-30 border-t bg-card pb-[env(safe-area-inset-bottom)]">
        <div className="grid grid-cols-3">
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.to === "/me"}
              data-testid={tab.testId}
              className={({ isActive }) =>
                cn(
                  "flex min-h-[60px] flex-col items-center justify-center gap-1 text-xs font-medium transition-colors",
                  isActive ? "text-primary" : "text-muted-foreground"
                )
              }
            >
              <tab.icon className="h-5 w-5" />
              {t(tab.labelKey)}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default EmployeeShell;
